import { CardNumberElement, CardExpiryElement, CardCvcElement, Elements, ElementsConsumer } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import * as React from "react";
import * as ReactDOM from "react-dom";
import Icon from "./icon";
import { resolve } from "inversify-react";
import PaymentService from "src/services/payment.service";
import IdentityService from "src/services/identity.service";
import images from "src/assets/images";

const PayPalButton = (window as any).paypal.Buttons.driver("react", { React, ReactDOM });
const stripePromise = loadStripe(
    "pk_test_51HHSvOFMvUlfXivHhKGaDLaIVa0mG7oWF7ZJVURUfcEv4n5bKtajPsKR9tHNuC7Lvf2LSkoD0dIe9rFKH4EDVOss00rzicDNqG",
);

interface Props {
    price: string | undefined;
    title: string;
    back?: boolean;
    onBack?: () => void;
    successMessage?: string;
    onCompleted: () => void;
    onError?: () => void;
    onProcessing?: () => void;
}

interface State {
    bitpayPayment: boolean;
    cardPayment: boolean;
    cardError: string;
    processingPayment: boolean;
    loadingStripe: boolean;
    processingStripePayment: boolean;
}

export default class PaymentComponent extends React.Component<Props> {
    @resolve
    public paymentService: PaymentService;
    @resolve
    public identityService: IdentityService;

    state: State = {
        bitpayPayment: false,
        cardPayment: false,
        cardError: "",
        processingPayment: false,
        loadingStripe: false,
        processingStripePayment: false,
    };

    stripeClientSecret: string;
    orderId: number;
    paypalOrderId: string;
    bitpayInvoiceId: string;

    componentDidMount(): void {
        window.addEventListener("message", (e) => this.handleBitpayEvent(e), false);
    }

    componentWillUnmount(): void {
        this.paymentService.reset();
        window.removeEventListener("message", (e) => this.handleBitpayEvent(e), false);
    }

    handleBitpayEvent(e: MessageEvent): void {
        if (this.state.bitpayPayment) {
            const payment_status = e.data.status;
            if (payment_status) {
                if (payment_status === "paid") {
                    this.setState({ processingPayment: true });
                    if (this.props.onProcessing) this.props.onProcessing();
                    this.polPaymentStatus();
                } else {
                    this.setState({ bitpayPayment: false });
                }
            }
        }
    }

    async createBitpayInvoice(): Promise<void> {
        this.setState({ bitpayPayment: true });

        const { invoiceId, id } = await this.paymentService.initiatePayment("bitpay", Number(this.props.price));

        this.orderId = id;
        this.bitpayInvoiceId = invoiceId || "";

        //REMOVE FOR PRODUCTION
        (window as any).bitpay.enableTestMode();
        (window as any).bitpay.onModalWillLeave(() => {
            this.setState({ bitpayPayment: false });
        });
        (window as any).bitpay.showInvoice(this.bitpayInvoiceId);
    }

    async createPaypalOrder(data, actions) {
        const { meta: { orderId }, id } = await this.paymentService.initiatePayment("paypal", Number(this.props.price));
        this.orderId = id;
        this.paypalOrderId = orderId || "";
        return orderId;
    }

    async showCardPayment(): Promise<void> {
        await this.setState({ cardPayment: true, loadingStripe: true });
        const { meta, id } = await this.paymentService.initiatePayment(
            "stripe",
            Number(this.props.price),
        );
        this.orderId = id;
        this.stripeClientSecret = meta.clientSecret || "";
        this.setState({ loadingStripe: false });
    }

    hideCardPayment(): void {
        this.setState({ cardPayment: false });
    }

    async handleStripePayment(event, stripe, elements): Promise<void> {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        await this.setState({ processingStripePayment: true });
        if (this.props.onProcessing) this.props.onProcessing();

        const cardElement = elements.getElement(CardNumberElement);

        const payload = await stripe.confirmCardPayment(this.stripeClientSecret, {
            payment_method: {
                card: cardElement,
            },
        });

        this.setState({ processingStripePayment: false });

        if (payload.error) {
            this.setState({ cardError: payload.error.message });
        } else {
            this.setState({ cardPayment: false, processingPayment: true });
            this.polPaymentStatus();
        }
    }

    onPaypalApprove(): void {
        this.setState({ processingPayment: true });
        if (this.props.onProcessing) this.props.onProcessing();
        this.polPaymentStatus();
    }

    polPaymentStatus(): void {
        this.paymentService.polPaymentStatus(
            this.orderId,
            () => this.success(),
            () => this.error(),
        );
    }

    success(): void {
        this.props.onCompleted();
        this.setState({ processingPayment: false, bitpayPayment: false });
    }

    error(): void {
        this.setState({ processingPayment: false, bitpayPayment: false });
        if (this.props.onError) this.props.onError();
    }

    cardInputStyle = {
        style: {
            base: {
                color: "black",
                fontWeight: 300,
                fontSize: "18px",
                textAlign: "center",

                "::placeholder": {
                    color: "#CFD7E0",
                },
            },
        },
    };

    render(): JSX.Element {
        const { price, title, back, onBack, successMessage } = this.props;
        const { cardPayment, processingPayment, bitpayPayment } = this.state;

        const choosePaymentMethod: JSX.Element = (
            <>
                <p>Choose a payment method</p>
                <div className="pay-buttons">
                    {PayPalButton && !bitpayPayment ? (
                        <>
                            <PayPalButton
                                style={{
                                    layout: "horizontal",
                                    tagline: false,
                                }}
                                createOrder={(data, actions) => this.createPaypalOrder(data, actions)}
                                onApprove={() => this.onPaypalApprove()}
                            />
                            {/*<input
                                type="image"
                                src="https://www.bitpay.com/cdn/en_US/bp-btn-pay.svg"
                                alt="Pay with BitPay"
                                className="bitpay-button"
                                onClick={() => this.createBitpayInvoice()}
                            />*/}
                            <button onClick={() => this.showCardPayment()} className="card-button">
                                Debit or Credit Card
                            </button>{" "}
                        </>
                    ) : (
                        <div className="processing-payment-container">
                            <img src={images.loading} className="loading-icon fa-spin" />
                        </div>
                    )}
                </div>
            </>
        );

        const provideCardInformation: JSX.Element = (
            <>
                {!this.state.loadingStripe ? (
                    <>
                        <p>Provide your card information</p>
                        <Elements stripe={stripePromise}>
                            <ElementsConsumer>
                                {({ elements, stripe }) => (
                                    <form onSubmit={(e) => this.handleStripePayment(e, stripe, elements)}>
                                        <div className="payment-form-input-container">
                                            <label>CARD NUMBER</label>
                                            <CardNumberElement className="payment-form-input" options={this.cardInputStyle} />
                                        </div>
                                        <div className="card-info-wrapper">
                                            <div className="payment-form-input-container">
                                                <label>CVC</label>
                                                <CardCvcElement className="payment-form-input" options={this.cardInputStyle} />
                                            </div>
                                            <div className="payment-form-input-container">
                                                <label>EXPIRES</label>
                                                <CardExpiryElement className="payment-form-input" options={this.cardInputStyle} />
                                            </div>
                                        </div>
                                        {this.state.cardError && <p className="credit-card-error">{this.state.cardError}</p>}
                                        <button type="submit" className="pay-button" disabled={this.state.processingStripePayment}>
                                            {this.state.processingStripePayment ? (
                                                <img src={images.loadingWhite} className="loading-icon fa-spin" />
                                            ) : (
                                                "Pay"
                                            )}
                                        </button>
                                        <button onClick={() => this.hideCardPayment()} className="back-button">
                                            Cancel
                                        </button>
                                    </form>
                                )}
                            </ElementsConsumer>
                        </Elements>
                    </>
                ) : (
                    <div className="processing-payment-container">
                        <img src={images.loading} className="loading-icon fa-spin" style={{ marginTop: "20px" }} />
                    </div>
                )}
            </>
        );

        return (
            <div className="payment-container">
                {back && !processingPayment && (
                    <div className="back" onClick={() => (onBack ? onBack() : undefined)}>
                        <Icon type="caret-left" />
                    </div>
                )}
                <h2>{title}</h2>
                {!this.paymentService.completed && !this.paymentService.error && (
                    <>
                        {!processingPayment ? (
                            <>
                                <div className="go-to-checkout-wrapper">
                                    <p className="price">
                                        <span className="bold">{price}</span> credits <span className="euros"> ({price} €)</span>
                                    </p>
                                </div>
                                {!cardPayment ? choosePaymentMethod : provideCardInformation}
                            </>
                        ) : (
                            <div className="processing-payment-container">
                                <img src={images.loading} className="loading-icon fa-spin" />
                                <p>Processing payment...</p>
                            </div>
                        )}
                    </>
                )}
                {this.paymentService.completed && (
                    <div className="payment-success-container">
                        <img src={images.success} />
                        <h2>Payment succeded</h2>
                        {!!successMessage && <p>{successMessage}</p>}
                    </div>
                )}
                {this.paymentService.error && (
                    <div className="payment-success-container">
                        <img src={images.fail} className="success-icon" />
                        <h2>Payment failed</h2>
                    </div>
                )}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/core/ui/payment-component.tsx